import { IterUtils, reverseArray } from "../../../../../../Common/IterableUtils";
import { ensureArrayIsNotEmpty } from "../../../../../../Common/TypingUtils";

import { ExternalEvent, ExternalEventPath } from "./ExternalEvent";
import { getExternalEventInfo, getExternalEventPath } from "./ExternalEvents";
import { ExternalHookFunction } from "./ExternalHookFunction";

interface HookStorage {
    children: { [path: string]: HookStorage };
    hooks: ExternalHookFunction[];
}

export interface IExternalEventListener {
    offExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): void;
    onExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): void;
    onceExternalEvent(pathArr: ExternalEventPath, x2: ExternalHookFunction): ExternalHookFunction;
}

export interface IExternalEventEmitter {
    triggerExternalEvent(event: ExternalEvent): void;
    triggerExternalEvents(events: ExternalEvent[]): void;
}

export interface IExternalEventManager extends IExternalEventListener, IExternalEventEmitter {
    hasExternalEventSubscriber(event: ExternalEventPath): boolean;
}

export class ExternalEventManager implements IExternalEventManager {
    private readonly hookStorage: HookStorage = { children: {}, hooks: [] };
    private readonly eventsQueue: ExternalEvent[] = [];
    private queueProcessing = false;

    public onExternalEvent(pathArr: ExternalEventPath, callback: ExternalHookFunction): void {
        const hookTreeItem = this.getPathNodes(pathArr, true).pop();
        if (hookTreeItem != undefined) {
            hookTreeItem.hooks.push(callback);
        }
    }

    public onceExternalEvent(pathArr: ExternalEventPath, callback: ExternalHookFunction): ExternalHookFunction {
        const hook: ExternalHookFunction = data => {
            this.offExternalEvent(pathArr, hook);
            callback(data);
        };

        const hookTreeItem = this.getPathNodes(pathArr, true).pop();
        if (hookTreeItem != undefined) {
            hookTreeItem.hooks.push(hook);
        }

        return hook;
    }

    public offExternalEvent(pathArr: ExternalEventPath, callback: ExternalHookFunction): void {
        const nodes = this.getPathNodes(pathArr);

        if (nodes.length !== pathArr.length + 1) {
            return;
        }
        ensureArrayIsNotEmpty(nodes);
        const node = IterUtils.last(nodes);
        const h = node.hooks;

        for (let i = 0; i < h.length; ++i) {
            if (h[i] === callback) {
                node.hooks.splice(i, 1);
                for (let j = pathArr.length - 1; j >= 0; --j) {
                    if (nodes[j + 1]?.hooks.length || Object.keys(nodes[j + 1]?.children ?? {}).length) {
                        break;
                    }
                    const childName = pathArr[j];
                    if (childName != undefined) {
                        delete nodes[j]?.children[childName];
                    }
                }
                break;
            }
        }
    }

    public triggerExternalEvent(event: ExternalEvent): void {
        this.triggerExternalEvents([event]);
    }

    public triggerExternalEvents(events: ExternalEvent[]): void {
        for (const event of events) {
            this.eventsQueue.push(event);
        }
        this.processQueue();
    }

    public hasExternalEventSubscriber(pathArr: ExternalEventPath): boolean {
        const nodes = this.getPathNodes(pathArr);
        return nodes.length === pathArr.length + 1;
    }

    private processQueue(): void {
        if (this.queueProcessing) {
            return;
        }
        this.queueProcessing = true;
        while (this.eventsQueue.length !== 0) {
            const event = this.eventsQueue.shift();
            if (event == undefined) {
                break;
            }
            const pathArr = getExternalEventPath(event);
            const eventData = getExternalEventInfo(event);

            const nodes = this.getPathNodes(pathArr);

            for (const node of reverseArray(nodes)) {
                for (const hook of node.hooks) {
                    hook(eventData);
                }
            }
        }
        this.queueProcessing = false;
    }

    private getPathNodes(pathArr: ExternalEventPath, createIfNotExists: boolean = false): HookStorage[] {
        let currentNode = this.hookStorage;
        const res = [currentNode];

        for (const pathItem of pathArr) {
            let nextNode = currentNode.children[pathItem];
            if (!nextNode) {
                if (createIfNotExists) {
                    nextNode = { children: {}, hooks: [] };
                    currentNode.children[pathItem] = nextNode;
                } else {
                    break;
                }
            }
            currentNode = nextNode;
            res.push(currentNode);
        }

        return res;
    }
}
